import { useCallback, useEffect, useRef, useState } from "react";
import { claimLoginKey, getAddinLoginUrl, requestLoginCode } from "../utils/addin-auth";
import { reportError } from "../utils/sentry-reporter";

const CLAIM_INTERVAL_MS = 2000;
const CLAIM_TIMEOUT_MS = 5 * 60 * 1000;

export type LoginHandoffPhase = "starting" | "waiting" | "error";

interface UseLoginHandoffOptions {
    onKey: (key: string) => Promise<void>;
    active?: boolean;
}

interface LoginHandoff {
    phase: LoginHandoffPhase;
    retry: () => void;
}

function openBrowser(url: string): void {
    try {
        Office.context.ui.openBrowserWindow(url);
    } catch {
        window.open(url, "_blank", "noopener,noreferrer");
    }
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function useLoginHandoff({ onKey, active = true }: UseLoginHandoffOptions): LoginHandoff {
    const [phase, setPhase] = useState<LoginHandoffPhase>("starting");
    const attemptRef = useRef(0);
    const mountedRef = useRef(true);
    const onKeyRef = useRef(onKey);
    onKeyRef.current = onKey;

    const isCurrent = useCallback((attempt: number) => mountedRef.current && attempt === attemptRef.current, []);

    const runHandoff = useCallback(async (attempt: number) => {
        try {
            const { code, clientSecret } = await requestLoginCode();
            if (!isCurrent(attempt)) return;
            openBrowser(getAddinLoginUrl(code));
            setPhase("waiting");

            const deadline = Date.now() + CLAIM_TIMEOUT_MS;
            let pollErrorReported = false;
            while (Date.now() < deadline) {
                try {
                    const result = await claimLoginKey(code, clientSecret);
                    if (!isCurrent(attempt)) return;
                    if (result.status === "ready") {
                        await onKeyRef.current(result.apiKey);
                        return;
                    }
                    if (result.status === "expired") break;
                } catch (pollErr) {
                    if (!pollErrorReported) {
                        pollErrorReported = true;
                        reportError(pollErr, { level: "warning", extra: { component: "useLoginHandoff", action: "claim_login_key" } });
                    }
                }
                await delay(CLAIM_INTERVAL_MS);
            }
            if (isCurrent(attempt)) setPhase("error");
        } catch (err) {
            reportError(err, { level: "warning", extra: { component: "useLoginHandoff", action: "start_login_handoff" } });
            if (isCurrent(attempt)) setPhase("error");
        }
    }, [isCurrent]);

    const retry = useCallback(() => {
        const attempt = ++attemptRef.current;
        setPhase("starting");
        void runHandoff(attempt);
    }, [runHandoff]);

    useEffect(() => {
        if (!active) return;
        mountedRef.current = true;
        retry();
        return () => {
            mountedRef.current = false;
        };
    }, [active, retry]);

    return { phase, retry };
}
